import {useEffect,useState} from 'react';
import './mobile-navigation.css';

const navigation=[
  {id:'top',zh:'料亭',en:'Ryotei',path:''},
  {id:'academy',zh:'學院',en:'Academy',path:'academy/'},
  {id:'characters',zh:'人物',en:'Characters',path:'characters/'},
  {id:'story',zh:'故事',en:'Story',path:'story/'},
  {id:'bloodline',zh:'血脈',en:'Bloodline',path:'bloodline/'},
  {id:'abyss',zh:'旬之淵',en:'The Abyss',path:'abyss/'}
];

function getLanguagePath(lang,path=''){
  return lang==='en'?`/en/${path}`:`/${path}`;
}

function isCurrentPage(pagePath,itemPath){
  if(itemPath==='')return pagePath==='';
  return pagePath===itemPath||pagePath.startsWith(itemPath);
}

function InternalLink({
  href,
  onNavigate,
  preserveScroll=false,
  onBeforeNavigate,
  children,
  ...props
}){
  const handleClick=(event)=>{
    if(
      event.defaultPrevented||
      event.button!==0||
      event.metaKey||
      event.ctrlKey||
      event.shiftKey||
      event.altKey
    )return;

    event.preventDefault();
    onBeforeNavigate?.();
    onNavigate(href,{preserveScroll});
  };

  return(
    <a href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  );
}

function LanguageLinks({lang,pagePath,onNavigate,onBeforeNavigate}){
  return(
    <div
      className="language"
      aria-label={lang==='zh-Hant'?'語言選擇':'Language selection'}
    >
      <InternalLink
        className={lang==='zh-Hant'?'active':''}
        href={getLanguagePath('zh-Hant',pagePath)}
        hrefLang="zh-Hant"
        lang="zh-Hant"
        onNavigate={onNavigate}
        onBeforeNavigate={onBeforeNavigate}
        preserveScroll
      >
        中
      </InternalLink>
      <i aria-hidden="true">/</i>
      <InternalLink
        className={lang==='en'?'active':''}
        href={getLanguagePath('en',pagePath)}
        hrefLang="en"
        lang="en"
        onNavigate={onNavigate}
        onBeforeNavigate={onBeforeNavigate}
        preserveScroll
      >
        EN
      </InternalLink>
    </div>
  );
}

function MenuToggle({open,english,onClick}){
  const label=open
    ?english?'Close navigation':'關閉選單'
    :english?'Open navigation':'開啟選單';

  return(
    <button
      className={`menu-toggle ${open?'is-open':''}`}
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-expanded={open}
      aria-controls="mobile-navigation"
    >
      <span/>
      <span/>
      <span/>
    </button>
  );
}

function MobileNavigation({open,lang,pagePath,onNavigate,onClose}){
  const english=lang==='en';

  return(
    <div
      className={`mobile-navigation ${open?'is-open':''}`}
      aria-hidden={!open}
    >
      <button
        className="mobile-navigation__backdrop"
        type="button"
        onClick={onClose}
        aria-label={english?'Close navigation':'關閉選單'}
        tabIndex={open?0:-1}
      />
      <nav
        className="mobile-navigation__panel"
        id="mobile-navigation"
        aria-label={english?'Mobile navigation':'行動版導覽'}
      >
        <div className="mobile-navigation__heading">
          <span>OMAKASE.HK</span>
          <small>{english?'SELECT A DESTINATION':'選擇前往之處'}</small>
        </div>
        <div className="mobile-navigation__links">
          {navigation.map((item,index)=>(
            <InternalLink
              key={item.id}
              href={getLanguagePath(lang,item.path)}
              onNavigate={onNavigate}
              onBeforeNavigate={onClose}
              tabIndex={open?0:-1}
              aria-current={isCurrentPage(pagePath,item.path)?'page':undefined}
            >
              <small>{String(index+1).padStart(2,'0')}</small>
              <span>{english?item.en:item.zh}</span>
              <i aria-hidden="true">↗</i>
            </InternalLink>
          ))}
        </div>
        <div className="mobile-navigation__footer">
          <span>{english?'LANGUAGE':'語言'}</span>
          <LanguageLinks
            lang={lang}
            pagePath={pagePath}
            onNavigate={onNavigate}
            onBeforeNavigate={onClose}
          />
        </div>
      </nav>
    </div>
  );
}

export default function Header({lang,pagePath='',onNavigate}){
  const [menuOpen,setMenuOpen]=useState(false);
  const english=lang==='en';

  useEffect(()=>{
    if(!menuOpen)return undefined;

    const previousOverflow=document.body.style.overflow;
    const closeOnEscape=(event)=>{
      if(event.key==='Escape')setMenuOpen(false);
    };

    document.body.style.overflow='hidden';
    window.addEventListener('keydown',closeOnEscape);

    return()=>{
      document.body.style.overflow=previousOverflow;
      window.removeEventListener('keydown',closeOnEscape);
    };
  },[menuOpen]);

  useEffect(()=>{
    const closeOnDesktop=()=>{
      if(window.innerWidth>1180)setMenuOpen(false);
    };

    window.addEventListener('resize',closeOnDesktop);
    return()=>window.removeEventListener('resize',closeOnDesktop);
  },[]);

  return(
    <>
      <header className="site-header">
        <InternalLink
          className="brand"
          href={getLanguagePath(lang)}
          onNavigate={onNavigate}
          onBeforeNavigate={()=>setMenuOpen(false)}
        >
          OMAKASE<span>.HK</span>
        </InternalLink>

        <nav aria-label={english?'Primary navigation':'主要導覽'}>
          {navigation.map((item)=>(
            <InternalLink
              key={item.id}
              href={getLanguagePath(lang,item.path)}
              onNavigate={onNavigate}
              aria-current={isCurrentPage(pagePath,item.path)?'page':undefined}
            >
              {english?item.en:item.zh}
            </InternalLink>
          ))}
        </nav>

        <div className="header-actions">
          <LanguageLinks
            lang={lang}
            pagePath={pagePath}
            onNavigate={onNavigate}
          />
          <MenuToggle
            open={menuOpen}
            english={english}
            onClick={()=>setMenuOpen((current)=>!current)}
          />
        </div>
      </header>

      <MobileNavigation
        open={menuOpen}
        lang={lang}
        pagePath={pagePath}
        onNavigate={onNavigate}
        onClose={()=>setMenuOpen(false)}
      />
    </>
  );
}